import { CariPicker } from "@/components/common/CariPicker";
import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useOffline } from "@/lib/net";
import { formatTRY } from "@/lib/format";
import { BANKS, Field, sel, todayIso, useAccounts, AccLabel, accColor } from "@/lib/finance";

const METHODS = { havale: "Havale/EFT", nakit: "Nakit", kart: "Kredi Kartı", cek: "Çek (verilen)", senet: "Senet (verilen)" };
const ACC_TYPES = { nakit: ["kasa"], havale: ["banka"], kart: ["banka"], cek: ["banka"], senet: [] };

export function useSuppliersLite() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/supplier-payments/suppliers").then(({ data }) => setList(data)).catch(() => {}); }, []);
  return list;
}

export default function SupplierPaymentDialog({ supplier, onClose, onDone, initialMethod }) {
  const accounts = useAccounts();
  const [f, setF] = useState({ supplier_id: supplier?.id || "", method: initialMethod || "havale", amount: "", date: todayIso(), account_id: "", serial_no: "", bank_name: "", due_date: "", note: "" });
  const [busy, setBusy] = useState(false);
  const off = useOffline();
  const [idem] = useState(() => crypto.randomUUID());
  const accs = useMemo(() => accounts.filter((a) => ACC_TYPES[f.method].includes(a.type)), [accounts, f.method]);
  useEffect(() => { if (!accs.find((a) => a.id === f.account_id)) setF((x) => ({ ...x, account_id: accs[0]?.id || "" })); }, [accs]); // eslint-disable-line react-hooks/exhaustive-deps
  const [picked, setPicked] = useState(null);
  const sup = picked ? { ...picked, balance: -picked.balance } : supplier;
  const instr = f.method === "cek" || f.method === "senet";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = { supplier_id: f.supplier_id, method: f.method, amount: Number(f.amount), date: f.date, account_id: f.account_id || null, note: f.note || null,
      instrument: instr ? { serial_no: f.serial_no, bank_name: f.bank_name, due_date: f.due_date } : null };
    try { const { data } = await api.post("/supplier-payments", body, { headers: { "Idempotency-Key": idem } }); toast.success(`${data.payment_no} kaydedildi${data.issued_no ? ` · ${data.issued_no}` : ""}`); onDone(data); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="supplier-payment-dialog">
        <DialogHeader><DialogTitle>Cariye Ödeme</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <Field label="Cari *" className="col-span-2">
            <CariPicker value={f.supplier_id} label={picked?.label || (supplier && `${supplier.code} · ${supplier.name}`)} disabled={!!supplier} testid="sp-supplier"
              onChange={(o) => { setPicked(o); setF((x) => ({ ...x, supplier_id: o?.id || "" })); }} />
          </Field>
          {sup && <div className="col-span-2 text-[12px] text-slate-600" data-testid="sp-balance">Güncel borcumuz: <b className="font-mono">{formatTRY(sup.balance)}</b></div>}
          <Field label="Ödeme Şekli"><select className={`${sel} w-full`} value={f.method} onChange={set("method")} data-testid="sp-method">{Object.entries(METHODS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
          <Field label="Tutar (TL)"><Input type="number" step="0.01" min="0.01" required value={f.amount} onChange={set("amount")} data-testid="sp-amount" /></Field>
          <Field label={instr ? "Düzenleme Tarihi" : "Ödeme Tarihi"}><Input type="date" value={f.date} max={todayIso()} onChange={set("date")} data-testid="sp-date" /></Field>
          {ACC_TYPES[f.method].length > 0 && <Field label={<AccLabel text={f.method === "cek" ? "Çek Hesabı (banka)" : "Çıkış Hesabı"} color={accColor(accs, f.account_id)} />}>
            <select className={`${sel} w-full`} value={f.account_id} onChange={set("account_id")} required={!instr} data-testid="sp-account">
              {f.method === "cek" && <option value="">Sonra seçilecek</option>}
              {accs.map((a) => <option key={a.id} value={a.id}>{`${a.name} · ${formatTRY(a.balance)}`}</option>)}
            </select></Field>}
          {instr && <>
            <Field label="Seri No *"><Input required value={f.serial_no} onChange={set("serial_no")} data-testid="sp-serial" /></Field>
            <Field label="Vade *"><Input type="date" required min={f.date} value={f.due_date} onChange={set("due_date")} data-testid="sp-due" /></Field>
            {f.method === "cek" && <Field label="Banka"><select className={`${sel} w-full`} value={f.bank_name} onChange={set("bank_name")} data-testid="sp-bank"><option value="">—</option>{BANKS.map((b) => <option key={b}>{b}</option>)}</select></Field>}
            <div className="col-span-2 text-[11px] text-slate-500">Verilen evrak tedarikçi borcunu bugün düşer; kasa/banka çıkışı vadede "Ödendi" işaretlenince oluşur ve nakit akışında vadesine göre görünür.</div>
          </>}
          <Field label="Not" className="col-span-2"><Input value={f.note} onChange={set("note")} data-testid="sp-note" /></Field>
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Vazgeç</Button>
            <Button type="submit" disabled={busy || off} className="bg-blue-600 hover:bg-blue-700" data-testid="sp-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Ödemeyi Kaydet</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
